/**
 * Terms and Conditions Page Component
 * Sidebar navigation using Ant Design Menu with project theme
 * Covers all data collection, security policies, and user rights
 * @module Terms
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Menu } from 'antd';
import {
  FileTextOutlined,
  UserOutlined,
  DatabaseOutlined,
  LockOutlined,
  SafetyOutlined,
  FormOutlined,
  CloudUploadOutlined,
  AuditOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  CopyrightOutlined,
  StopOutlined,
  ExclamationCircleOutlined,
  GlobalOutlined,
  MailOutlined,
  MenuOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styles from './Terms.module.css';

/** Section keys (stable reference for scroll spy) */
const SECTION_KEYS = [
  'introduction', 'userAccounts', 'dataCollection', 'authentication',
  'cookies', 'surveys', 'fileStorage', 'auditLogging', 'dataRetention',
  'dataDeletion', 'intellectualProperty', 'acceptableUse', 'liability',
  'governingLaw', 'contact'
];

/** Section icons map */
const SECTION_ICONS = {
  introduction: <FileTextOutlined />,
  userAccounts: <UserOutlined />,
  dataCollection: <DatabaseOutlined />,
  authentication: <LockOutlined />,
  cookies: <SafetyOutlined />,
  surveys: <FormOutlined />,
  fileStorage: <CloudUploadOutlined />,
  auditLogging: <AuditOutlined />,
  dataRetention: <ClockCircleOutlined />,
  dataDeletion: <DeleteOutlined />,
  intellectualProperty: <CopyrightOutlined />,
  acceptableUse: <StopOutlined />,
  liability: <ExclamationCircleOutlined />,
  governingLaw: <GlobalOutlined />,
  contact: <MailOutlined />
};

/**
 * Terms and Conditions Page
 */
const Terms = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('introduction');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mainContentRef = useRef(null);

  // Scroll spy – uses stable SECTION_KEYS array
  const handleScroll = useCallback(() => {
    for (let i = SECTION_KEYS.length - 1; i >= 0; i--) {
      const el = document.getElementById(SECTION_KEYS[i]);
      if (el && el.getBoundingClientRect().top <= 150) {
        setActiveSection(SECTION_KEYS[i]);
        break;
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = useCallback((key) => {
    const element = document.getElementById(key);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(key);
      setMobileMenuOpen(false);
    }
  }, []);

  // Build Ant Design Menu items
  const menuItems = useMemo(() =>
    SECTION_KEYS.map((key) => ({
      key,
      icon: SECTION_ICONS[key],
      label: t(`terms.sections.${key}.title`),
    })), [t]);

  return (
    <div className={styles.termsPage}>
      {/* Mobile menu toggle */}
      <button
        className={styles.mobileMenuToggle}
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle navigation menu"
      >
        {mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
        <span>{t('terms.tableOfContents')}</span>
      </button>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className={styles.mobileOverlay}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${mobileMenuOpen ? styles.open : ''}`}>
        <div className={styles.sidebarHeader}>
          <div className={styles.titleRow}>
            <h1 className={styles.sidebarTitle}>{t('terms.title')}</h1>
          </div>
          <span className={styles.lastUpdated}>
            {t('terms.lastUpdated', { date: 'April 10, 2026' })}
          </span>
        </div>

        <Menu
          mode="inline"
          selectedKeys={[activeSection]}
          items={menuItems}
          onClick={({ key }) => scrollToSection(key)}
          className={styles.sidebarMenu}
        />
      </aside>

      {/* Main Content */}
      <main className={styles.mainContent} ref={mainContentRef}>
        {/* Introduction box */}
        <div className={styles.introBox}>
          <p>{t('terms.introText')}</p>
        </div>

        {/* Content Sections */}
        {SECTION_KEYS.map((key, index) => (
          <section
            key={key}
            id={key}
            className={styles.contentSection}
          >
            <h2 className={styles.sectionTitle}>
              <span className={styles.sectionNumber}>{index + 1}</span>
              {t(`terms.sections.${key}.title`)}
            </h2>
            <div
              className={styles.sectionContent}
              dangerouslySetInnerHTML={{
                __html: t(`terms.sections.${key}.content`)
              }}
            />
          </section>
        ))}

        {/* Contact Banner */}
        <div className={styles.contactBanner}>
          <h3>{t('terms.questionsTitle')}</h3>
          <p>{t('terms.questionsText')}</p>
          <a
            href="mailto:alarouri@wsa.rwth-aachen.de"
            className={styles.contactEmail}
          >
            <MailOutlined />
            alarouri@wsa.rwth-aachen.de
          </a>
        </div>
      </main>
    </div>
  );
};

export default Terms;
